import styles from './ChangeInvestigationStatusDialog.module.scss';
import { useTranslation } from 'react-i18next';
import {
  VdsButton,
  VdsDialog,
  VdsDialogActionFooter,
  VdsDialogBody,
  VdsDialogHeader,
  VdsDialogModal,
  VdsNotification,
} from '@volt/volt-extended-components-react';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useAppDispatch } from '@customer/store/hooks';
import {
  investigationsApi,
  InvestigationStatus,
  InvestigationStatusUpdateModel,
  usePostStatusMutation,
} from '@customer/store/apis/investigationsApi';
import { QueryTag } from '@customer/store/api';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import { SuccessNotificationContext } from '@dmc/ui';
import { TranslationNamespaceBackend } from '@cas/utils/translationHelpers';

const MaxCharacters = 2000;

interface ChangeInvestigationStatusDialogProps {
  investigationId: number;
  status: InvestigationStatus;
  isOpen: boolean;
  onClose: () => void;
  onCancel: () => void;
}

export function ChangeInvestigationStatusDialog({
  investigationId,
  status,
  isOpen,
  onClose,
  onCancel,
}: ChangeInvestigationStatusDialogProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLVdsDialogElement>(null);

  const dispatch = useAppDispatch();
  const { showSuccessMessage } = useContext(SuccessNotificationContext);

  const [postStatus] = usePostStatusMutation();

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState(isOpen);

  const schema = useMemo(() => {
    return z.object({
      status: z.nativeEnum(InvestigationStatus),
      noteText: z
        .string()
        .max(MaxCharacters, {
          message: t('validation.maxCharacters', { count: MaxCharacters }),
        })
        .optional(),
    });
  }, [t]);

  const defaultValues: InvestigationStatusUpdateModel = {
    status: status,
    noteText: undefined,
  };

  const { handleSubmit, control, reset } = useForm<InvestigationStatusUpdateModel>({
    defaultValues,
    resolver: zodResolver(schema),
    mode: 'all',
    reValidateMode: 'onBlur',
  });

  // If the parent controls have changed to isOpen=true, then open the dialog and update the local state.
  if (isOpen && !open) {
    ref.current?.open();
    setOpen(true);
  }

  const onClosed = useCallback(() => {
    // Ensure the status dropdown and the mutation is reset (to remove errors/inconsistency)
    dispatch(
      investigationsApi.util.invalidateTags([
        { type: QueryTag.Investigation, id: investigationId },
      ]),
    );
    setErrorMessage(undefined);
    reset();

    // Update parent state
    onClose();

    // Update local state
    setOpen(false);
  }, [dispatch, investigationId, onClose, reset]);

  const onSubmitInternal = useCallback(
    async (model: InvestigationStatusUpdateModel): Promise<void> => {
      if (typeof investigationId === 'undefined') return;

      await postStatus({
        investigationId: investigationId.toString(),
        status: status,
        noteText: model.noteText,
      }).then(response => {
        // Error: Update the panel UI with an error message.
        if (response.error) {
          setErrorMessage(t('PostInvestigationStatusError'));
        }
        // Success: Clear any error message, show the success toast, and close the panel.
        else {
          setErrorMessage(undefined);
          showSuccessMessage(t('PostInvestigationStatusSuccess'));
          ref?.current?.close();
        }
      });
    },
    [investigationId, postStatus, status, t, showSuccessMessage],
  );

  return (
    <div className={styles.wrapper}>
      <VdsDialog
        ref={ref}
        key="change-investigation-status"
        padding="spacing-07"
        backdrop={true}
        width="600px"
        onClosed={onClosed}
      >
        <VdsDialogModal>
          <VdsDialogHeader slot="header" show-close-button={false}>
            {t('InvestigationStatus')}
          </VdsDialogHeader>
          <VdsDialogBody slot="body" className={styles.body}>
            {errorMessage && (
              <VdsNotification variant="inline" status="danger" className={styles.notification}>
                <span slot="body">{t('FailedToUpdateInvestigationStatus')}</span>
              </VdsNotification>
            )}

            <div className={styles.message}>
              Change status to {t(`Enum.InvSts.${status}`, { ns: TranslationNamespaceBackend })}
            </div>

            <CasFormInput
              control={control}
              name="noteText"
              label={t('NoteOptional')}
              element="textarea"
            />
          </VdsDialogBody>
          <VdsDialogActionFooter slot="footer" class="vds__dialog__footer--end">
            <VdsButton variant="secondary" close-dialog="" onClick={onCancel}>
              {t('Cancel')}
            </VdsButton>
            <VdsButton variant="primary" onClick={() => handleSubmit(onSubmitInternal)()}>
              {t('ChangeStatus')}
            </VdsButton>
          </VdsDialogActionFooter>
        </VdsDialogModal>
      </VdsDialog>
    </div>
  );
}

export default ChangeInvestigationStatusDialog;
